/**
 * PLATEAU 3D Tiles material policy (RFC-113 Q1).
 * Centralised so forensic-audit fixes land in one place without touching
 * cache, CRS alignment, or layer sync in Giro3DAdapter.
 */
import * as THREE from 'three';

/** Normalize PLATEAU glTF default roughness (1.0) for consistent LOD brightness. */
export const PLATEAU_DEFAULT_ROUGHNESS = 0.65;

/** Coarse untextured parent tile base color — closer to average texture luminance. */
export const PLATEAU_COARSE_BASE_COLOR = 0xcccccc;

export const PLATEAU_PATCHED_FLAG = '__geosoftPlateauPatched';

export interface PlateauMaterialPolicyOptions {
  /** Forensic hook: when true, skip vertexColors on textured meshes (A/B test). */
  texturedSkipVertexColors?: boolean;
  /** Forensic hook: force FrontSide on closed textured building meshes. */
  texturedFrontSideOnly?: boolean;
  /** Forensic hook: also patch normal/emissive maps colorSpace. */
  patchAllTextureMaps?: boolean;
}

const DEFAULT_POLICY: PlateauMaterialPolicyOptions = {
  texturedSkipVertexColors: false,
  texturedFrontSideOnly: false,
  patchAllTextureMaps: false,
};

let activePolicy: PlateauMaterialPolicyOptions = { ...DEFAULT_POLICY };

/** Apply forensic-audit driven overrides (call once when investigation completes). */
export function setPlateauMaterialPolicy(options: Partial<PlateauMaterialPolicyOptions>): void {
  activePolicy = { ...DEFAULT_POLICY, ...options };
}

export function getPlateauMaterialPolicy(): Readonly<PlateauMaterialPolicyOptions> {
  return activePolicy;
}

function patchTextureColorSpace(map: THREE.Texture | null | undefined): void {
  if (!map) return;
  if (map.colorSpace !== THREE.SRGBColorSpace) {
    map.colorSpace = THREE.SRGBColorSpace;
  }
}

function hasGeometryAttribute(geometry: unknown, name: string): boolean {
  const attrs = (geometry as { attributes?: Record<string, unknown> })?.attributes;
  return Boolean(attrs?.[name]);
}

/** Untextured / coarse LOD parent tiles. */
export function patchPlateauCoarseMaterial(
  mat: THREE.Material & Record<string, unknown>,
  geometry?: unknown,
): void {
  const normalAttr = (geometry as { attributes?: { normal?: { count?: number } } })?.attributes?.normal;
  const hasSmoothNormals = !!(normalAttr && (normalAttr.count ?? 0) > 0);
  const hasVertexColors =
    hasGeometryAttribute(geometry, 'color') || hasGeometryAttribute(geometry, 'COLOR_0');

  if (!hasSmoothNormals) {
    (mat as unknown as THREE.MeshStandardMaterial).flatShading = true;
  }

  (mat as unknown as THREE.MeshStandardMaterial).side = THREE.DoubleSide;

  if (!hasVertexColors && 'color' in mat && mat.color instanceof THREE.Color) {
    mat.color.setHex(PLATEAU_COARSE_BASE_COLOR);
  }

  if (hasVertexColors) {
    (mat as unknown as THREE.MeshStandardMaterial).vertexColors = true;
    if ('color' in mat && mat.color instanceof THREE.Color) {
      mat.color.setHex(0xffffff);
    }
  }

  const std = mat as unknown as THREE.MeshStandardMaterial;
  if (std.metalness === 0 && std.roughness === 1) {
    std.metalness = 0.08;
    std.roughness = PLATEAU_DEFAULT_ROUGHNESS;
    if (std.envMapIntensity !== undefined) std.envMapIntensity = 0.3;
  } else if (typeof std.roughness === 'number' && std.roughness >= 0.95) {
    std.roughness = PLATEAU_DEFAULT_ROUGHNESS;
  }

  mat.opacity = 1;
  mat.transparent = false;

  mat.needsUpdate = true;
}

/** Textured PLATEAU photogrammetry tiles (per-tile on load-model). */
export function patchPlateauTexturedMaterial(
  mat: THREE.Material & Record<string, unknown>,
  geometry?: unknown,
): void {
  const std = mat as unknown as THREE.MeshStandardMaterial;
  const policy = activePolicy;

  patchTextureColorSpace(std.map);
  if (policy.patchAllTextureMaps) {
    patchTextureColorSpace(std.normalMap);
    patchTextureColorSpace(std.emissiveMap);
    patchTextureColorSpace(std.aoMap);
  }

  if (std.color) std.color.setHex(0xffffff);

  if (typeof std.metalness === 'number') std.metalness = Math.min(std.metalness, 0.05);
  if (typeof std.roughness === 'number' && std.roughness >= 0.95) {
    std.roughness = PLATEAU_DEFAULT_ROUGHNESS;
  }
  if (typeof std.envMapIntensity === 'number' && std.envMapIntensity === 0) {
    std.envMapIntensity = 0.35;
  }

  const hasVertexColors =
    !policy.texturedSkipVertexColors &&
    (hasGeometryAttribute(geometry, 'color') || hasGeometryAttribute(geometry, 'COLOR_0'));
  if (hasVertexColors) {
    std.vertexColors = true;
  }

  if (policy.texturedFrontSideOnly) {
    std.side = THREE.FrontSide;
  }

  mat.opacity = 1;
  mat.transparent = false;

  mat.needsUpdate = true;
}

/** Patch one streamed glTF material from a b3dm tile. Idempotent via userData flag. */
export function patchPlateauStreamedMaterial(
  mat: THREE.Material & Record<string, unknown> | null | undefined,
  geometry?: unknown,
): boolean {
  if (!mat || mat.userData?.[PLATEAU_PATCHED_FLAG]) return false;

  const hasTexture = (mat as unknown as THREE.MeshStandardMaterial).map != null;
  if (hasTexture) {
    patchPlateauTexturedMaterial(mat, geometry);
  } else {
    patchPlateauCoarseMaterial(mat, geometry);
  }

  mat.userData[PLATEAU_PATCHED_FLAG] = true;
  return true;
}

/** Walk a loaded tile scene graph and patch all mesh materials. */
export function patchPlateauTileScene(scene: {
  traverse?: (cb: (child: unknown) => void) => void;
}): number {
  if (!scene?.traverse) return 0;
  let count = 0;
  scene.traverse((child: unknown) => {
    const mesh = child as { isMesh?: boolean; material?: unknown; geometry?: unknown };
    if (!mesh?.isMesh || !mesh.material) return;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of materials) {
      if (patchPlateauStreamedMaterial(mat as THREE.Material & Record<string, unknown>, mesh.geometry)) {
        count += 1;
      }
    }
  });
  return count;
}

export type PlateauForensicCause = 'source' | 'viewer' | 'crs' | 'lod' | 'cache' | 'layer';

export interface PlateauSymptomVerdict {
  symptom: string;
  cause: PlateauForensicCause;
  note?: string;
}

/**
 * Apply material policy from forensic investigation output.
 * Call once when the separate audit agent delivers its verdict table.
 */
export function applyPlateauForensicPolicy(verdicts: readonly PlateauSymptomVerdict[]): void {
  const bySymptom = new Map(verdicts.map((v) => [v.symptom, v]));
  const options: Partial<PlateauMaterialPolicyOptions> = {};

  const darkTextures = bySymptom.get('dark_textures') ?? bySymptom.get('dark_flat_buildings');
  const uvOnWall = bySymptom.get('uv_on_wall') ?? bySymptom.get('h_on_wall');

  if (darkTextures?.cause === 'viewer') {
    options.patchAllTextureMaps = true;
  }
  if (uvOnWall?.cause === 'viewer') {
    options.texturedFrontSideOnly = true;
  }

  if (Object.keys(options).length > 0) {
    setPlateauMaterialPolicy(options);
    console.log('[plateauTileMaterials] Forensic policy applied:', options);
  } else {
    console.log('[plateauTileMaterials] Forensic policy: material hooks unchanged (tone mapping handled separately)', {
      verdictCount: verdicts.length,
    });
  }
}

/** Verdict table from PLATEAU_TEXTURE_UV_FORENSIC_AUDIT.md (2026-05-24). */
export const PLATEAU_FORENSIC_VERDICTS: readonly PlateauSymptomVerdict[] = [
  { symptom: 'h_on_wall', cause: 'source', note: 'PLATEAU converter UV atlas; no viewer UV ops' },
  { symptom: 'dark_textures', cause: 'viewer', note: 'NoToneMapping + roughness cap; fixed via ACES + material policy' },
  { symptom: 'white_lod_parents', cause: 'lod', note: 'REPLACE refinement + mismatched coarse/fine PBR' },
  { symptom: 'roads_elevated', cause: 'crs', note: 'Shared anchor fixed; residual is physical pavement altitude' },
  { symptom: 'sparse_furniture', cause: 'layer', note: 'Layer mount + sparse data (~1 tile / 19k m²)' },
  { symptom: 'sparse_bridges', cause: 'source', note: 'Only ~125 b3dm in Shinjuku dataset' },
];

export function applyPlateauForensicAuditDefaults(): void {
  applyPlateauForensicPolicy(PLATEAU_FORENSIC_VERDICTS);
}
